// CSpell:ignore Lato
import { createTheme, hexToRgb } from "@mui/material";
import "@mui/x-data-grid/themeAugmentation";
// eslint-disable-next-line import/no-empty-named-blocks
import type {} from "@mui/x-date-pickers/themeAugmentation";
declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    DefaultButtonBackground?: string;
  }
  interface ColorLevels {
    900: string;
    800: string;
    700: string;
    600: string;
    500: string;
    400: string;
    300: string;
    200: string;
    100: string;
    50: string;
  }

  interface Palette {
    blueGray: ColorLevels;
    progressBar: {
      light: string;
      medium: string;
      dark: string;
      darker: string;
    };
    customWarning: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
      border: string;
    };
    customError: {
      light: string;
      main: string;
      border: string;
    };
    customSuccess: {
      light: string;
      main: string;
    };
    icon: {
      background: string;
    };
    linehaulStatus: {
      neutral: string;
      expedite: string;
      /** FB follow-up / OSD (purple); no other palette token matches. */
      osd: string;
    };
    /** FB follow-up status count colors (UC/CC OFD dashboard summary). */
    ucccOfdFbFollowUp: {
      delivered: string;
      expedite: string;
      ofd: string;
      osd: string;
      pendingAppt: string;
      pendingPod: string;
      serviceFailure: string;
      noStatus: string;
    };
    /** On-time performance KPI info tooltip (UC/CC OFD dashboard). */
    ucccOfdOnTimeKpiTooltip: {
      green: string;
      yellow: string;
      red: string;
    };
    shades: {
      "4p": string;
    };
  }
  interface PaletteOptions {
    blueGray?: Palette["blueGray"];
    progressBar?: Palette["progressBar"];
    customWarning?: Palette["customWarning"];
    customError?: Palette["customError"];
    customSuccess?: Palette["customSuccess"];
    icon?: Palette["icon"];
    linehaulStatus?: Palette["linehaulStatus"];
    ucccOfdFbFollowUp?: Palette["ucccOfdFbFollowUp"];
    ucccOfdOnTimeKpiTooltip?: Palette["ucccOfdOnTimeKpiTooltip"];
    shades?: Palette["shades"];
  }
  interface Color {
    border?: string;
  }
}

// NOTE
// We need to add an eslint-disable-next-line to import of
// "@mui/x-date-pickers/themeAugmentation" above as it's just a type definition file.
// Link to issue in Github: https://github.com/mui/material-ui/issues/27704#issuecomment-897434343
export const AppTheme = createTheme({
  palette: {
    primary: {
      dark: "#014A80",
      main: "#026AB7",
      light: "#44AFFF",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#0167B3",
      light: "#BEDBFF",
    },
    background: {
      default: "#FAFAFA",
      DefaultButtonBackground: "rgba(0, 0, 2055, 0.08)",
      paper: "#FFFFFF",
    },
    info: {
      main: "rgba(25, 118, 210, 0.14)",
      light: "#1976D214",
    },
    success: {
      light: "#3cb371", // MediumSeaGreen
      main: "#008000", // Green
    },
    customWarning: {
      light: "#fff3e0",
      main: "#ed6c02",
      dark: "#e65100",
      contrastText: "#fff",
      border: "#FFE0B2",
    },
    customError: {
      light: "#FFEBEE",
      main: "#D32F2F",
      border: "#FFCDD2",
    },
    customSuccess: {
      light: "#ECF6ED",
      main: "#2E7D32",
    },
    grey: {
      "900": "#000000de",
      "700": "#00000098",
      "600": "#00000091",
      "500": "#00000080",
      "200": "#F0F0F0",
      "100": "#0000001f",
      "50": "#00000009",
      border: "#E0E0E0",
    },
    action: {
      selected: "rgba(25, 118, 210, 0.12)",
    },
    progressBar: {
      light: "#CFD8DC",
      medium: "#90A4AE",
      dark: "#607D8B",
      darker: "#455A64",
    },
    icon: {
      background: "#0E48DC",
    },
    blueGray: {
      "900": "#263238",
      "800": "#37474F",
      "700": "#455A64",
      "600": "#546E7A",
      "500": "#607D8B",
      "400": "#78909C",
      "300": "#90A4AE",
      "200": "#B0BEC5",
      "100": "#CFD8DC",
      "50": "#ECEFF1",
    },
    linehaulStatus: {
      neutral: "#E6E6E6",
      expedite: "#F0E36E",
      osd: "#65007B",
    },
    ucccOfdFbFollowUp: {
      delivered: "#3F832B",
      expedite: "#CE7F00",
      ofd: "#0047C3",
      osd: "#65007B",
      pendingAppt: "#A82700",
      pendingPod: "#002EC4",
      serviceFailure: "#B00000",
      noStatus: "#707070DE",
    },
    ucccOfdOnTimeKpiTooltip: {
      green: "#7DE557",
      yellow: "#E8DD74",
      red: "#F27474",
    },
    shades: {
      "4p": "rgba(25, 118, 210, 0.04)",
    },
  },
  typography: {
    fontFamily: "Lato",
  },
  components: {
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "capitalize",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "capitalize",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "capitalize",
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        rowsPerPageOptions: [20, 50, 100, 500, 1000],
        density: "comfortable",
        initialState: {
          columns: { columnVisibilityModel: { id: false, active: false } },
          pagination: { pageSize: 100 },
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      // Increase the minWidth of the time selection sections to give space for scrolling
      styleOverrides: {
        root: {
          minWidth: "75px",
        },
        item: {
          minWidth: "67px",
        },
      },
    },
  },
});

export const hexToRgba = (hex: string, alpha = 1) => {
  const rgb = hexToRgb(hex); // "rgb(255, 87, 51)"
  return rgb.replace("rgb", "rgba").replace(")", `, ${alpha})`);
};
